import { computed } from 'vue';
import { storeToRefs } from 'pinia';

import { useManagerCan } from '@/composables/users/useManagerCan';
import { useUserStore } from '@/stores/user';
import type { CorpUserSegmentation, SegmentationDimension } from '@/types';

export type SegmentedFamily =
  | 'cards'
  | 'vehicles'
  | 'users'
  | 'teams'
  | 'cost_centers'
  | 'projects'
  | 'card_templates';

const SCOPED_FAMILIES: Record<SegmentationDimension, readonly SegmentedFamily[]> = {
  cost_center: ['cards', 'vehicles', 'cost_centers'],
  project: ['cards', 'vehicles', 'projects'],
  team: ['cards', 'users', 'teams'],
  product: ['cards', 'card_templates'],
};

const CREATABLE_DIMENSION_PERMISSION = {
  cost_center: 'cost_centers',
  project: 'projects',
  team: 'teams',
} as const;

type CreatableDimension = keyof typeof CREATABLE_DIMENSION_PERMISSION;

const GLOBAL: CorpUserSegmentation = { dimension: null, values: [] };

export function useActorSegmentation() {
  const { meData } = storeToRefs(useUserStore());
  const { can } = useManagerCan();

  const segmentation = computed<CorpUserSegmentation>(() => meData.value?.segmentation ?? GLOBAL);
  const dimension = computed(() => segmentation.value.dimension);
  const values = computed(() => segmentation.value.values);
  const isGlobal = computed(() => dimension.value === null);

  const scopes = (family: SegmentedFamily): boolean =>
    dimension.value !== null && (SCOPED_FAMILIES[dimension.value]?.includes(family) ?? false);

  const canCreateIn = (target: CreatableDimension): boolean =>
    can(CREATABLE_DIMENSION_PERMISSION[target], 'create') && dimension.value !== target;

  return { dimension, values, isGlobal, scopes, canCreateIn };
}
